import React from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { format, isValid } from 'date-fns';
import {
  HiOutlineClock,
  HiOutlineCalendar,
  HiOutlineDocumentText,
  HiOutlineChevronRight,
} from 'react-icons/hi';
import { FaCheck } from 'react-icons/fa';
import { formatCurrency } from 'Core/utils/numberFormat';
import CreditCardLogos from 'Core/components/CreditCardLogos';

function formatTransactionDateTime(createdAt) {
  if (createdAt === undefined || createdAt === null) return '';
  // Unix timestamp in seconds (e.g. 1770122250.210639) – convert to milliseconds
  const seconds = typeof createdAt === 'string' ? parseFloat(createdAt, 10) : Number(createdAt);
  const date = new Date(seconds * 1000);
  return isValid(date) ? format(date, "MMM d, yyyy 'at' h:mm a") : '';
}

export function TransactionHistory({ transactions }) {
  const { t } = useTranslation();

  return transactions.length === 0 ? null : (
    <div className="mb-8">
      {/* Modern Section Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-gray-100">
          <HiOutlineClock className="w-5 h-5 text-gray-600" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 m-0">
          {t('directSales.history.header')}
        </h2>
        <span className="px-2.5 py-0.5 bg-gray-100 text-gray-600 text-sm font-medium rounded-full">
          {transactions.length}
        </span>
      </div>

      {/* Transaction Cards */}
      <div className="space-y-3">
        {transactions.map((transaction) => {
          const {
            created_at,
            card_action,
            amount,
            brand,
            last4digits,
            receipt: { url: receiptUrl },
          } = transaction;

          const formattedDateTime = formatTransactionDateTime(created_at);

          const hasDecimals = !Number.isSafeInteger(amount);
          const isCapture = card_action.toLowerCase() === 'capture';

          return (
            <div
              className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              key={created_at}
            >
              {/* Main Content */}
              <div className="p-4 md:p-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  {/* Status Badge + Amount (same row on mobile) */}
                  <div className="flex flex-row items-center gap-3 sm:gap-4 flex-1 min-w-0 md:justify-start justify-between">
                    <div className="flex-shrink-0">
                      <span
                        className={classnames(
                          'inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold',
                          {
                            'bg-green-100 text-green-800 border border-green-200': isCapture,
                            'bg-amber-100 text-amber-800 border border-amber-200': !isCapture,
                          }
                        )}
                      >
                        {isCapture && <FaCheck className="w-4 h-4 mr-1.5 flex-shrink-0" />}
                        {t(`common.${card_action.toLowerCase()}`)}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <span className="text-2xl sm:text-3xl font-bold text-gray-900">
                        {formatCurrency(amount, { withDecimals: hasDecimals })}
                      </span>
                    </div>
                  </div>

                  {/* Card Info */}
                  <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 rounded-lg border border-gray-100">
                    <CreditCardLogos brand={brand} size="medium" />
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500 font-medium">Card ending in</span>
                      <span className="font-semibold text-gray-900">•••• {last4digits}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-4 sm:px-5 py-3 bg-gray-50/70 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <HiOutlineCalendar className="w-4 h-4 flex-shrink-0" />
                  {formattedDateTime}
                </div>
                <a
                  href={receiptUrl}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-blue-700 transition-colors group"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <HiOutlineDocumentText className="w-4 h-4 flex-shrink-0" />
                  {t('common.viewReceipt')}
                  <HiOutlineChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5 flex-shrink-0" />
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
